import React, { useRef, useState } from "react";
import Logo from "@/components/brand/Logo";
import { gsap, useIsoLayoutEffect, prefersReducedMotion } from "@/lib/motion";

const SEEN_KEY = "mk_intro_seen";

function shouldPlay() {
  if (typeof window === "undefined" || prefersReducedMotion()) return false;
  try {
    return sessionStorage.getItem(SEEN_KEY) !== "1";
  } catch {
    return true;
  }
}

/* Evaluated once per page load so other components (the hero) can time their
   entrance after the curtain lifts. */
export const introState = { playing: shouldPlay(), duration: 1.75 };

/* First visit per session: the mark assembles itself — the hexagon is drawn,
   filled, the parcel drops into its slot, the wordmark slides in — then the
   curtain lifts. The page is already rendered underneath (never gated), and any
   click, key or scroll skips straight to it. */
export default function IntroLoader() {
  const [active, setActive] = useState(introState.playing);
  const rootRef = useRef(null);

  useIsoLayoutEffect(() => {
    if (!active || !rootRef.current) return undefined;
    try {
      sessionStorage.setItem(SEEN_KEY, "1");
    } catch {}

    const root = rootRef.current;
    const hex = root.querySelector(".mk-hex");
    const box = root.querySelector(".mk-box");
    // Animate the paths, not their groups: the groups carry the wordmark's offset transform.
    const misk = root.querySelector(".mk-word-misk > path");
    const cargo = root.querySelector(".mk-word-cargo > path");
    const len = hex.getTotalLength ? hex.getTotalLength() : 3000;
    const fill = hex.getAttribute("fill");

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        onComplete: () => {
          introState.playing = false;
          setActive(false);
        },
      });
      gsap.set(hex, { fillOpacity: 0, stroke: fill, strokeWidth: 12, strokeDasharray: len, strokeDashoffset: len });
      gsap.set(box, { y: -520, opacity: 0 });
      gsap.set([misk, cargo], { opacity: 0, x: -60 });

      tl.to(hex, { strokeDashoffset: 0, duration: 0.7, ease: "power3.inOut" })
        .to(hex, { fillOpacity: 1, strokeWidth: 0, duration: 0.35, ease: "power2.out" }, 0.5)
        .to(box, { y: 0, opacity: 1, duration: 0.6, ease: "expo.out" }, 0.55)
        .to(misk, { opacity: 1, x: 0, duration: 0.6 }, 0.8)
        .to(cargo, { opacity: 1, x: 0, duration: 0.6 }, 0.9)
        .to(".intro-logo", { y: -24, opacity: 0, duration: 0.5, ease: "power3.in" }, 1.55)
        .to(root, { clipPath: "inset(0% 0% 100% 0%)", duration: 0.8, ease: "expo.inOut" }, 1.6);

      const skip = () => tl.progress(1);
      window.addEventListener("wheel", skip, { passive: true, once: true });
      window.addEventListener("touchstart", skip, { passive: true, once: true });
      window.addEventListener("keydown", skip, { once: true });
      root.addEventListener("click", skip, { once: true });
      return () => {
        window.removeEventListener("wheel", skip);
        window.removeEventListener("touchstart", skip);
        window.removeEventListener("keydown", skip);
      };
    }, root);

    return () => ctx.revert();
  }, [active]);

  if (!active) return null;

  return (
    <div
      ref={rootRef}
      aria-hidden="true"
      className="fixed inset-0 z-intro flex items-center justify-center bg-[var(--forest-950)] dot-grid"
      style={{ clipPath: "inset(0% 0% 0% 0%)" }}
    >
      <div className="intro-logo w-[min(72vw,520px)]" dir="ltr">
        <Logo tone="dark" className="w-full h-auto overflow-visible" title="" />
      </div>
    </div>
  );
}
